import { isBillingEnabledFor } from '@/config';
import PrivateRoute from '@/modules/auth/components/PrivateRoute';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import BillingDetailsEdit from '@/modules/billing/pages/BillingDetailsEdit';
import BillingHome from '@/modules/billing/pages/BillingHome';
import InvoiceDetails from '@/modules/billing/pages/InvoiceDetails';
import InvoiceList from '@/modules/billing/pages/InvoiceList';
import PaymentMethodSetup from '@/modules/billing/pages/PaymentMethodAdd';
import PaymentMethodList from '@/modules/billing/pages/PaymentMethodList';
import UpgradePlan from '@/modules/billing/pages/UpgradePlan';
import UpgradePlanPaymentSetup from '@/modules/billing/pages/UpgradePlanPaymentSetup';
import {
  BillingDetailsEditPath,
  BillingHomePath,
  BillingInvoiceDetailsPath,
  BillingInvoiceListPath,
  BillingPaymentMethodAddPath,
  BillingPaymentMethodListPath,
  BillingUpgradePlanPath,
  BillingUpgradePlanPaymentSetupPath,
} from '@/modules/billing/paths';
import OrderList from '@/modules/order/pages/OrderList';
import { OrderDetailsPath, OrderListPath } from '@/modules/order/paths';
import {
  ProductAdd,
  ProductCategoryAdd,
  ProductCategoryEdit,
  ProductCategoryList,
  ProductEdit,
  ProductImport,
  ProductList,
} from '@/modules/product/pages';
import {
  ProductAddPath,
  ProductCategoryAddPath,
  ProductCategoryEditPath,
  ProductCategoryListPath,
  ProductCategoryProductListPath,
  ProductEditPath,
  ProductImportPath,
  ProductListPath,
} from '@/modules/product/paths';
import Currency from '@/modules/settings/pages/Currency';
import Language from '@/modules/settings/pages/Language';
import Notification from '@/modules/settings/pages/Notification';
import Password from '@/modules/settings/pages/Password';
import { StoreAddressType } from '@/modules/store/models/Store';
import { UserRole } from '@/modules/store/models/UserRole';
import Cart from '@/modules/store/pages/Cart';
import CheckoutReview from '@/modules/store/pages/CheckoutReview';
import CheckoutSuccess from '@/modules/store/pages/CheckoutSuccess';
import StoreOrderDetails from '@/modules/store/pages/OrderDetails';
import ProductDetails from '@/modules/store/pages/ProductDetails';
import Address from '@/modules/store/pages/Settings/Address';
import Profile from '@/modules/store/pages/Settings/Profile';
import Settings from '@/modules/store/pages/Settings/Settings';
import SupplierAdd from '@/modules/store/pages/Settings/SupplierAdd';
import SupplierEdit from '@/modules/store/pages/Settings/SupplierEdit';
import SupplierList from '@/modules/store/pages/Settings/SupplierList';
import Shop from '@/modules/store/pages/Shop';
import {
  CartPath,
  CheckoutReviewPath,
  CheckoutSuccessPath,
  ProductDetailsPath,
  SettingsSupplierAddPath,
  SettingsSupplierEditPath,
  ShopPath,
  StoreSettingsPath,
} from '@/modules/store/paths';
import { Routes as ReactRoutes, Route } from 'react-router-dom';

const Routes = () => {
  const { role } = useAuth();
  const isStoreManager = role === UserRole.StoreManager;
  const isStoreBillingEnabled = isBillingEnabledFor('store');

  return (
    <ReactRoutes>
      <Route element={<PrivateRoute />}>
        <Route path={CartPath} Component={Cart} />
        <Route path={CheckoutReviewPath} Component={CheckoutReview} />
        <Route path={CheckoutSuccessPath.path} Component={CheckoutSuccess} />
        <Route path={ShopPath.path} Component={Shop} />
        <Route path={ProductDetailsPath.path} Component={ProductDetails} />
        {isStoreManager && (
          <>
            <Route path={OrderDetailsPath.path} Component={StoreOrderDetails} />
            <Route path={OrderListPath} Component={OrderList} />
            <Route Component={Settings}>
              <Route path={StoreSettingsPath.profile} Component={Profile} />
              <Route path={StoreSettingsPath.address} Component={Address} />
              <Route
                path={StoreSettingsPath.businessAddress}
                element={<Address addressType={StoreAddressType.BUSINESS} />}
              />
              <Route
                path={StoreSettingsPath.deliveryAddress}
                element={<Address addressType={StoreAddressType.DELIVERY} />}
              />
              <Route path={StoreSettingsPath.currency} Component={Currency} />
              <Route path={StoreSettingsPath.language} Component={Language} />
              <Route path={StoreSettingsPath.notification} Component={Notification} />
              <Route path={StoreSettingsPath.password} Component={Password} />
              <Route path={StoreSettingsPath.supplier} Component={SupplierList} />
              <Route path={SettingsSupplierEditPath.path} Component={SupplierEdit} />
              <Route path={SettingsSupplierAddPath} Component={SupplierAdd} />
              <Route path={ProductListPath} Component={ProductList} />
              <Route path={ProductEditPath.path} Component={ProductEdit} />
              <Route path={ProductAddPath} Component={ProductAdd} />
              <Route path={ProductImportPath} Component={ProductImport} />
              <Route path={ProductCategoryListPath} Component={ProductCategoryList} />
              <Route path={ProductCategoryEditPath.path} Component={ProductCategoryEdit} />
              <Route path={ProductCategoryAddPath} Component={ProductCategoryAdd} />
              <Route path={ProductCategoryProductListPath.path} Component={ProductList} />
              {isStoreBillingEnabled && (
                <>
                  <Route path={BillingHomePath} Component={BillingHome} />
                  <Route path={BillingInvoiceListPath} Component={InvoiceList} />
                  <Route path={BillingInvoiceDetailsPath.path} Component={InvoiceDetails} />
                  <Route path={BillingDetailsEditPath} Component={BillingDetailsEdit} />
                  <Route path={BillingUpgradePlanPath} Component={UpgradePlan} />
                  <Route
                    path={BillingUpgradePlanPaymentSetupPath}
                    Component={UpgradePlanPaymentSetup}
                  />
                  <Route path={BillingPaymentMethodListPath} Component={PaymentMethodList} />
                  <Route path={BillingPaymentMethodAddPath} Component={PaymentMethodSetup} />
                </>
              )}
            </Route>
          </>
        )}
      </Route>
    </ReactRoutes>
  );
};

export default Routes;
